import { createStore, compose } from "redux";
import { PortRootObject } from "../hooks/port_types";
import { RouteRootObject } from "../hooks/route_types";
import { VesselRootObject } from "../hooks/ferry_types";
import { Ferry } from "../hooks/useFerries";

type RestAction<
  TName extends keyof StoreState,
  TValue extends StoreState[TName] = StoreState[TName]
> =
  | {
      type: "INSERT_MANY";
      resourceType: TName;
      data: TValue[];
    }
  | {
      type: "INSERT_ONE";
      resourceType: TName;
      data: TValue;
    };

type RestState<T> = Record<number, T | undefined>;

export interface StoreState {
  ports: RestState<PortRootObject>;
  vessels: RestState<VesselRootObject>;
  routes: RestState<RouteRootObject>;
  ferries: RestState<Ferry>;
}

const initialState: StoreState = {
  ports: {},
  vessels: {},
  routes: {},
  ferries: {}
};

export type RestResource =
  | PortRootObject
  | VesselRootObject
  | RouteRootObject
  | Ferry;

const mergeState = <TState extends RestResource>(
  data: RestState<TState>,
  newData: TState[]
): RestState<TState> => {
  return newData.reduce(
    (prev, curr) => ({
      ...prev,
      [(curr as any).id || (curr as any).imo]: curr
    }),
    data
  );
};

type Action =
  | RestAction<"ports">
  | RestAction<"vessels">
  | RestAction<"ferries">
  | RestAction<"routes">;

const rootReducer = (
  state: StoreState = initialState,
  action: Action
): StoreState => {
  switch (action.type) {
    case "INSERT_MANY": {
      const resourceType = action.resourceType;
      return {
        ...state,
        [resourceType]: mergeState(
          state[resourceType] as any,
          action.data as any
        )
      };
    }
    case "INSERT_ONE": {
      return {
        ...state,
        [action.resourceType]: mergeState(state[action.resourceType] as any, [
          action.data as any
        ])
      };
    }
    default:
      return state;
  }
};

const composeWithDevTools =
  (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

export const store = createStore(rootReducer, composeWithDevTools());
